const SPATIAL_CONTRACT_VERSION = 2;
const SOURCE_MODE = 'source';
const OVERRIDE_MODE = 'override';
const PROMOTE_VISIBLE_ANCHORS = 'promoteVisibleAnchors';
const RETAIN_VISIBLE_ANCHORS_AS_CONTEXT = 'retainVisibleAnchorsAsContext';
const LEGACY_INHERIT = 'inherit';
const LEGACY_NONE = 'none';
const LEGACY_DIM = 'dim';

function text(value) {
  if (value === null || value === undefined) return null;
  const normalized = String(value).trim();
  return normalized || null;
}

function uniqueText(values) {
  const result = [];
  (Array.isArray(values) ? values : []).forEach((value) => {
    const normalized = text(value);
    if (normalized && result.indexOf(normalized) === -1) result.push(normalized);
  });
  return result;
}

function normalizedNoTarget(value) {
  return value === RETAIN_VISIBLE_ANCHORS_AS_CONTEXT
    ? RETAIN_VISIBLE_ANCHORS_AS_CONTEXT
    : PROMOTE_VISIBLE_ANCHORS;
}

function normalizedIncludeOwningContext(value) {
  return value !== false;
}

function explicitOwningContext(value) {
  return normalizedIncludeOwningContext(value)
    ? {}
    : { includeOwningContext: false };
}

/** Map the existing viewer switch onto the typed v2 no-target vocabulary. */
export function spatialFocusNoTargetForFallback(enabled) {
  return enabled === false
    ? RETAIN_VISIBLE_ANCHORS_AS_CONTEXT
    : PROMOTE_VISIBLE_ANCHORS;
}

function refusal(code, message) {
  return { focus: null, legacy: false, error: { code, message } };
}

function bindingMap(spatial) {
  return new Map((spatial.bindings || [])
    .filter(binding => binding && text(binding.bindingKey))
    .map(binding => [text(binding.bindingKey), binding]));
}

function roleAllowed(binding, role) {
  return Array.isArray(binding.allowedRoles) && binding.allowedRoles.indexOf(role) !== -1;
}

function legacyColumnToken(value) {
  const normalized = text(value);
  return normalized ? normalized.toLowerCase().replace(/[^a-z0-9]/g, '') : null;
}

function legacyBindingColumns(binding) {
  const label = text(binding?.label);
  const labelColumn = label && label.includes(' [') ? label.slice(0, label.indexOf(' [')) : null;
  const bindingKey = text(binding?.bindingKey);
  return [
    binding?.source?.column,
    binding?.source?.fieldKey,
    labelColumn,
    bindingKey?.startsWith('legacy:') ? bindingKey.slice('legacy:'.length) : null,
  ].map(legacyColumnToken).filter(Boolean);
}

function legacyColumnBinding(spatial, source, role) {
  const requested = legacyColumnToken(source);
  if (!requested) return null;
  return (spatial.bindings || []).find(binding => (
    binding
    && roleAllowed(binding, role)
    && legacyBindingColumns(binding).includes(requested)
  )) || null;
}

function completeRowAnchorBinding(spatial) {
  return (spatial.bindings || []).find(binding => (
    binding
    && roleAllowed(binding, 'context')
    && binding.semantic === 'anchor'
    && binding.siblingScope === 'completeRowSet'
  )) || null;
}

/**
 * Translate the old column-named widget POI settings into the published binding vocabulary.
 *
 * This is a read-time compatibility seam only: no physical column enters the resolver request and
 * newly-saved widgets continue to persist revision-pinned `spatialFocus` intent. A dim legacy POI
 * uses the complete row anchor set as context, matching the old anomaly/CML-over-component scene.
 */
export function legacySpatialFocusOverride(spatial, widgetConfig = null) {
  if (!hasSpatialBindingCapability(spatial)) return null;

  const source = text(widgetConfig?.poiSource);
  if (!source || source.toLowerCase() === LEGACY_INHERIT) return null;
  if (source.toLowerCase() === LEGACY_NONE) {
    return {
      mode: OVERRIDE_MODE,
      sourceRevision: text(spatial.sourceRevision),
      targets: [],
      contexts: [],
    };
  }

  const target = legacyColumnBinding(spatial, source, 'target');
  if (!target) return null;

  const contexts = [];
  const contextSource = text(widgetConfig?.contextSource);
  if (contextSource && contextSource.toLowerCase() === LEGACY_INHERIT) {
    contexts.push(...(spatial.defaultFocus?.contexts || []).map(selector => ({
      bindingKey: selector.bindingKey,
      selection: selector.selection,
    })));
  } else if (contextSource && contextSource.toLowerCase() !== LEGACY_NONE) {
    const context = legacyColumnBinding(spatial, contextSource, 'context');
    if (context) contexts.push({ bindingKey: context.bindingKey, selection: 'all' });
  } else if (!contextSource && text(widgetConfig?.poiPaintMode)?.toLowerCase() === LEGACY_DIM) {
    const anchor = completeRowAnchorBinding(spatial);
    if (anchor) contexts.push({ bindingKey: anchor.bindingKey, selection: 'all' });
  }

  return {
    mode: OVERRIDE_MODE,
    sourceRevision: text(spatial.sourceRevision),
    targets: [{ bindingKey: target.bindingKey }],
    contexts,
    noTarget: PROMOTE_VISIBLE_ANCHORS,
  };
}

function normalizedTagTypes(binding, selector) {
  const requested = uniqueText(selector.tagTypes);
  if (!requested.length) return { values: null, error: null };
  const allowed = binding.filter?.kind === 'tagType'
    ? uniqueText(binding.filter.values)
    : [];
  const invalid = requested.find(tagType => allowed.indexOf(tagType) === -1);
  if (invalid) {
    return {
      values: null,
      error: {
        code: 'invalidSpatialTagType',
        message: `Tag type "${invalid}" is not available for binding "${binding.bindingKey}".`,
      },
    };
  }
  return { values: requested, error: null };
}

function normalizeTargets(spatial, selectors) {
  const bindings = bindingMap(spatial);
  const targets = [];
  for (const selector of Array.isArray(selectors) ? selectors : []) {
    const key = text(selector?.bindingKey);
    const binding = bindings.get(key);
    if (!binding || !roleAllowed(binding, 'target')) {
      return { values: null, error: unknownBinding(key, 'target') };
    }
    const tagTypes = normalizedTagTypes(binding, selector);
    if (tagTypes.error) return { values: null, error: tagTypes.error };
    const target = { bindingKey: key };
    if (tagTypes.values) target.tagTypes = tagTypes.values;
    targets.push(target);
  }
  return { values: targets, error: null };
}

function unknownBinding(key, role) {
  return {
    code: 'unknownSpatialBinding',
    message: `Binding "${key || '(blank)'}" is not available as ${role}.`,
  };
}

function normalizeContexts(spatial, selectors, targets) {
  const bindings = bindingMap(spatial);
  const contexts = [];
  for (const selector of Array.isArray(selectors) ? selectors : []) {
    const key = text(selector?.bindingKey);
    const binding = bindings.get(key);
    if (!binding || !roleAllowed(binding, 'context')) {
      return { values: null, error: unknownBinding(key, 'context') };
    }
    const selection = text(selector.selection);
    if (selection !== 'all' && selection !== 'remaining') {
      return {
        values: null,
        error: {
          code: 'invalidSpatialContextSelection',
          message: `Context binding "${key}" needs an all or remaining selection.`,
        },
      };
    }
    const sameTarget = targets.find(target => target.bindingKey === key);
    if ((selection === 'remaining' && !sameTarget?.tagTypes?.length)
      || (selection === 'all' && sameTarget)) {
      return {
        values: null,
        error: {
          code: 'incompatibleSpatialContextSelection',
          message: `Context selection "${selection}" is incompatible with binding "${key}".`,
        },
      };
    }
    contexts.push({ bindingKey: key, selection });
  }
  return { values: contexts, error: null };
}

/** True only for the published binding/default-focus catalogue contract. */
export function hasSpatialBindingCapability(spatial) {
  return Number(spatial?.spatialContractVersion) === SPATIAL_CONTRACT_VERSION
    && !!text(spatial?.rowKey)
    && !!text(spatial?.sourceRevision)
    && Array.isArray(spatial?.bindings)
    && !!spatial?.defaultFocus
    && Array.isArray(spatial.defaultFocus.targets)
    && Array.isArray(spatial.defaultFocus.contexts);
}

/**
 * Derive the typed v2 policy without ever copying catalogue internals or physical columns.
 * Old descriptors intentionally return a legacy decision during the mixed-version rollout.
 */
export function resolveSpatialFocusPolicy(spatial, widgetFocus = null) {
  if (!hasSpatialBindingCapability(spatial)) {
    if (widgetFocus?.mode === OVERRIDE_MODE) {
      return refusal(
        'spatialBindingsUnavailable',
        'This widget override needs a published spatial binding catalogue.',
      );
    }
    return { focus: null, legacy: true, error: null };
  }

  const diagnostic = text(spatial.defaultFocusDiagnostic);
  if (diagnostic) {
    return refusal(diagnostic, 'Review the source default 3D focus and republish it.');
  }
  if (!widgetFocus || widgetFocus.mode !== OVERRIDE_MODE) {
    return {
      focus: { spatialContractVersion: SPATIAL_CONTRACT_VERSION, mode: SOURCE_MODE },
      legacy: false,
      error: null,
    };
  }
  if (text(widgetFocus.sourceRevision) !== text(spatial.sourceRevision)) {
    return refusal(
      'staleSpatialFocusOverride',
      'This widget override was authored against an older source revision.',
    );
  }

  const targets = normalizeTargets(spatial, widgetFocus.targets);
  if (targets.error) return { focus: null, legacy: false, error: targets.error };
  const contexts = normalizeContexts(spatial, widgetFocus.contexts, targets.values);
  if (contexts.error) return { focus: null, legacy: false, error: contexts.error };

  const focus = {
    spatialContractVersion: SPATIAL_CONTRACT_VERSION,
    mode: OVERRIDE_MODE,
    targets: targets.values,
    contexts: contexts.values,
    ...explicitOwningContext(widgetFocus.includeOwningContext),
  };
  if (widgetFocus.noTarget === PROMOTE_VISIBLE_ANCHORS
    || widgetFocus.noTarget === RETAIN_VISIBLE_ANCHORS_AS_CONTEXT) {
    focus.noTarget = widgetFocus.noTarget;
  }
  return { focus, legacy: false, error: null };
}

/** The switch value implied by authored source/widget intent unless the session chose explicitly. */
export function spatialFocusFallbackEnabled(spatial, widgetFocus = null, sessionOverride = null) {
  if (typeof sessionOverride === 'boolean') return sessionOverride;
  if (!hasSpatialBindingCapability(spatial)) return true;
  const authored = widgetFocus?.mode === OVERRIDE_MODE
    ? widgetFocus.noTarget
    : spatial.defaultFocus?.noTarget;
  return normalizedNoTarget(authored) === PROMOTE_VISIBLE_ANCHORS;
}

/** The owning-context choice implied by source/widget intent; missing typed fields default on. */
export function spatialFocusOwningContextEnabled(spatial, widgetFocus = null) {
  if (!hasSpatialBindingCapability(spatial)) return true;
  const authored = widgetFocus?.mode === OVERRIDE_MODE
    ? widgetFocus.includeOwningContext
    : spatial.defaultFocus?.includeOwningContext;
  return normalizedIncludeOwningContext(authored);
}

/**
 * Apply a session-local switch to typed intent. A differing source-mode choice must become an
 * explicit, revision-validated override because the API intentionally forbids noTarget on source
 * mode. Published selectors are copied as intent only; physical catalogue metadata never crosses.
 */
export function resolveSpatialFocusSessionPolicy(
  spatial,
  widgetFocus = null,
  sessionOverride = null,
) {
  const authored = resolveSpatialFocusPolicy(spatial, widgetFocus);
  if (authored.error || authored.legacy || typeof sessionOverride !== 'boolean') return authored;

  const currentNoTarget = authored.focus.mode === OVERRIDE_MODE
    ? normalizedNoTarget(authored.focus.noTarget)
    : normalizedNoTarget(spatial.defaultFocus?.noTarget);
  const requestedNoTarget = spatialFocusNoTargetForFallback(sessionOverride);
  if (requestedNoTarget === currentNoTarget) return authored;

  const selectors = authored.focus.mode === OVERRIDE_MODE
    ? authored.focus
    : spatial.defaultFocus;
  return resolveSpatialFocusPolicy(spatial, {
    mode: OVERRIDE_MODE,
    sourceRevision: text(spatial.sourceRevision),
    targets: selectors.targets,
    contexts: selectors.contexts,
    noTarget: requestedNoTarget,
    ...explicitOwningContext(spatialFocusOwningContextEnabled(spatial, widgetFocus)),
  });
}

/** Whitelist a widget override for storage; the catalogue descriptor itself is never persisted. */
export function spatialFocusOverrideForStorage(spatial, widgetFocus) {
  if (widgetFocus?.mode !== OVERRIDE_MODE) return { value: null, error: null };
  const resolved = resolveSpatialFocusPolicy(spatial, widgetFocus);
  if (resolved.error) return { value: null, error: resolved.error };
  const focus = resolved.focus;
  return {
    value: {
      mode: OVERRIDE_MODE,
      sourceRevision: text(spatial.sourceRevision),
      targets: focus.targets,
      contexts: focus.contexts,
      ...(focus.noTarget ? { noTarget: focus.noTarget } : {}),
      ...explicitOwningContext(focus.includeOwningContext),
    },
    error: null,
  };
}

/** Build the one resolver envelope and reject mixed legacy/v2 policy channels locally. */
export function buildSpatialResolveRequest({
  scope,
  focus = null,
  useMasterTagAsPoiFallback,
} = {}) {
  if (!scope) {
    return { request: null, error: { code: 'spatialScopeUnavailable', message: 'A spatial scope is required.' } };
  }
  if (focus && scope.kind === 'entities') {
    return conflict('focusEntityScopeConflict', 'Entity focus uses its canonical server policy.');
  }
  if (focus && Array.isArray(scope.registerRoles) && scope.registerRoles.length) {
    return conflict('focusRegisterRolesConflict', 'Focus and legacy Register roles cannot be combined.');
  }
  if (focus && useMasterTagAsPoiFallback !== undefined) {
    return conflict('focusLegacyFallbackConflict', 'Focus and the legacy Master Tag fallback cannot be combined.');
  }

  const request = { scope };
  if (focus) request.focus = focus;
  if (!focus && useMasterTagAsPoiFallback === false) {
    request.useMasterTagAsPoiFallback = false;
  }
  return { request, error: null };
}

function conflict(code, message) {
  return { request: null, error: { code, message } };
}

export const SpatialFocusContractVersion = SPATIAL_CONTRACT_VERSION;
export const SpatialFocusNoTargetPolicy = Object.freeze({
  PromoteVisibleAnchors: PROMOTE_VISIBLE_ANCHORS,
  RetainVisibleAnchorsAsContext: RETAIN_VISIBLE_ANCHORS_AS_CONTEXT,
});
